import React from 'react';
import { useSelector } from 'react-redux';
import {
  Carousel,
  CarouselItem,
  IconButton,
  Modal,
  ModalBody,
  ModalHeader,
} from '@americanexpress/dls-react';
import { IconPause, IconPicture, IconPlay } from '@americanexpress/dls-icons';
import {
  useLoungeDetailsCarouselImages,
  useLoungeById,
} from '@/hooks/loungesHooks/loungesHelperHooks';
import { useCommonContent } from '@/hooks/oneCmsIntlHooks/oneCmsIntlHelperHooks';
import {
  useDetachedControlsPosition,
  useHeroVideoStateSync,
} from '@/hooks/useLoungeDetailsHeroCarouselHooks';
import { LoungeTypes } from '@/constants';
import { useRouteParams } from '@/context/routeParamsContext';
import { getOneCmsLoungeFinderImagesBaseUrl } from '@/selectors/oneCmsSelectors';
import { getEnvLevel } from '@/selectors/originSelectors';
import styles from './LoungeDetails.module.scss';
// import LoungeTypeBadge from '../common/LoungeTypeBadge';

const buildHeroSlides = ({ images, heroVideoUrl }) => {
  const imageSlides = images.map((image) => ({
    slideType: 'image',
    ...image,
  }));

  if (!heroVideoUrl) {
    return imageSlides;
  }

  return [
    {
      slideType: 'video',
      src: heroVideoUrl,
      alt: `Sidecar by the Centurion Lounge hero video`,
    },
    ...imageSlides,
  ];
};

const renderHeroSlides = ({
  heroSlides,
  loungeName,
  isHeroVideoPlaying,
  setIsHeroVideoPlaying,
  heroVideoFallbackImage,
  heroVideoFallbackImageMobile,
}) =>
  heroSlides.map((slide, i) => {
    if (slide.slideType === 'video') {
      return (
        <CarouselItem
          key={slide.src}
          id="lounge-hero-video"
          className="tw-aspect-carouselSmall tw-w-full tw-min-w-full sm_tw-aspect-carouselBig"
        >
          <div className="tw-relative tw-h-full tw-w-full">
            {heroVideoFallbackImage && (
              <picture>
                <source
                  media="(max-width: 567px)"
                  srcSet={heroVideoFallbackImageMobile}
                />
                <img
                  src={heroVideoFallbackImage}
                  alt={slide.alt}
                  className="tw-absolute tw-inset-0 tw-h-full tw-w-full tw-object-cover"
                />
              </picture>
            )}
            <video
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              className={`tw-pointer-events-none tw-absolute tw-inset-0 tw-h-full tw-w-full tw-object-cover tw-transition-opacity tw-duration-300 ${
                isHeroVideoPlaying ? 'tw-opacity-100' : 'tw-opacity-0'
              }`}
              aria-label={slide.alt}
              poster={heroVideoFallbackImage}
              onPlaying={() => setIsHeroVideoPlaying(true)}
              onTimeUpdate={(event) => {
                if (event.currentTarget.currentTime > 0) {
                  setIsHeroVideoPlaying(true);
                }
              }}
              onWaiting={() => setIsHeroVideoPlaying(false)}
              onStalled={() => setIsHeroVideoPlaying(false)}
              onError={() => setIsHeroVideoPlaying(false)}
            >
              <source src={slide.src} type="video/quicktime" />
              <source src={slide.src} />
            </video>
          </div>
        </CarouselItem>
      );
    }

    return (
      <CarouselItem
        key={slide.imgId}
        id={slide.imgId}
        className="tw-aspect-carouselSmall tw-w-full tw-min-w-full sm_tw-aspect-carouselBig"
      >
        <img
          src={slide.imgUrl}
          alt={`#${i + 1} of ${heroSlides.length} slides of ${loungeName}`}
          className="tw-w-full tw-object-cover"
        />
      </CarouselItem>
    );
  });

const DetachedControls = ({
  detachedControlsPosition,
  isAutoPlay,
  onToggleAutoplay,
  showGalleryButton,
  onOpenGallery,
}) => {
  if (!detachedControlsPosition) {
    return null;
  }

  return (
    <div
      className={styles.detachedControlsOverlay}
      style={detachedControlsPosition}
    >
      <IconButton
        screenReaderLabel={
          isAutoPlay ? 'Pause carousel autoplay' : 'Play carousel autoplay'
        }
        variant="secondary"
        className={styles.galleryControlButton}
        onClick={onToggleAutoplay}
      >
        {isAutoPlay ? (
          <IconPause color="brand" size="md" />
        ) : (
          <IconPlay color="brand" size="md" />
        )}
      </IconButton>
      {showGalleryButton && (
        <IconButton
          screenReaderLabel="Open image gallery"
          variant="secondary"
          className={styles.galleryControlButton}
          onClick={onOpenGallery}
        >
          <IconPicture color="brand" size="md" />
        </IconButton>
      )}
    </div>
  );
};

const GalleryModal = ({
  isGalleryOpen,
  onClose,
  loungeName,
  images,
  startId,
}) => {
  if (!isGalleryOpen) {
    return null;
  }

  return (
    <Modal
      id="lounge-hero-gallery-modal"
      aria-labelledby="lounge-hero-gallery-modal-title"
      aria-describedby="lounge-hero-gallery-modal-content"
      onClose={onClose}
      innerContainerClassName="tw-max-w-screen-lg tw-m-0 sm_tw-m-4"
    >
      <ModalHeader
        id="lounge-hero-gallery-modal-title"
        closeButtonProps={{ reactlytics: { disable: true } }}
      >
        <h2>{loungeName}</h2>
      </ModalHeader>
      <ModalBody
        id="lounge-hero-gallery-modal-content"
        className="tw-bg-black tw-p-0"
      >
        <Carousel
          key={startId || 'lounge-hero-gallery'}
          id="lounge-hero-gallery"
          defaultCurrentSlideId={startId}
          isNavigationOnSlides={true}
          isSlideFullWidth={true}
        >
          {images.map(({ imgUrl, imgId }, i) => (
            <CarouselItem key={imgId} id={imgId}>
              <img
                src={imgUrl}
                alt={`#${i + 1} of ${images.length} images of ${loungeName}`}
                className="tw-max-h-[80vh] tw-w-full tw-object-contain"
              />
            </CarouselItem>
          ))}
        </Carousel>
      </ModalBody>
    </Modal>
  );
};

const getSidecarHeroVideoUrl = ({ lounges, envLevel }) => {
  const sidecarConfig = lounges?.sidecar;
  if (!sidecarConfig) return null;

  // E0 and E1 use E1 URL
  if (envLevel === 'E0' || envLevel === 'E1') {
    return sidecarConfig.heroVideoUrlE1 || null;
  }

  // E2 uses E2 URL
  if (envLevel === 'E2') {
    return sidecarConfig.heroVideoUrlE2 || null;
  }

  // E3 uses E3 URL
  return sidecarConfig.heroVideoUrlE3 || null;
};

const LoungeDetailsHeroCarousel = () => {
  const { loungeId } = useRouteParams();
  const lounge = useLoungeById(loungeId);
  const images = useLoungeDetailsCarouselImages(loungeId);
  const isSidecarLounge = lounge?.experience_type === LoungeTypes.SIDECAR;
  const heroCarouselRef = React.useRef(null);
  const nativeAutoplayButtonRef = React.useRef(null);
  const hasUserToggledAutoplayRef = React.useRef(false);
  const [currentSlideId, setCurrentSlideId] = React.useState('');
  const [galleryStartSlideId, setGalleryStartSlideId] = React.useState('');
  const [detachedControlsPosition, setDetachedControlsPosition] =
    React.useState(null);
  // Keep autoplay enabled for all lounge types through the August 12th release.
  const [isAutoPlay, setIsAutoPlay] = React.useState(true);
  const [isGalleryOpen, setIsGalleryOpen] = React.useState(false);
  const [isHeroVideoPlaying, setIsHeroVideoPlaying] = React.useState(false);
  const { lounges } = useCommonContent();
  const oneCmsImagesBaseUrl = useSelector(getOneCmsLoungeFinderImagesBaseUrl);
  const envLevel = useSelector(getEnvLevel);
  const resolvedSidecarVideoUrl = getSidecarHeroVideoUrl({
    lounges,
    envLevel,
  });
  const heroVideoUrl = isSidecarLounge ? resolvedSidecarVideoUrl : null;

  const heroSlides = React.useMemo(
    () =>
      buildHeroSlides({
        images,
        heroVideoUrl,
      }),
    [heroVideoUrl, images]
  );
  const sidecarFallbackImage = lounges?.sidecar?.heroVideoFallbackImage;
  const sidecarFallbackMobileImage =
    lounges?.sidecar?.heroVideoFallbackImageMobile;

  const heroVideoFallbackImage =
    isSidecarLounge && sidecarFallbackImage
      ? `${oneCmsImagesBaseUrl}${sidecarFallbackImage}`
      : '';
  const heroVideoFallbackImageMobile =
    isSidecarLounge && sidecarFallbackMobileImage
      ? `${oneCmsImagesBaseUrl}${sidecarFallbackMobileImage}`
      : heroVideoFallbackImage;

  const syncNativeAutoplayToState = React.useCallback(() => {
    const nativeAutoplayButton = nativeAutoplayButtonRef.current;

    if (!nativeAutoplayButton) {
      return;
    }

    const controlText = (nativeAutoplayButton.textContent || '').toLowerCase();
    const isNativeAutoplayOn = controlText.includes(
      'pause automatic slide show'
    );

    if (isNativeAutoplayOn !== isAutoPlay) {
      nativeAutoplayButton.click();
    }
  }, [isAutoPlay]);

  React.useEffect(() => {
    if (!hasUserToggledAutoplayRef.current) {
      // Keep autoplay enabled for all lounge types through the August 12th release.
      setIsAutoPlay(true);
    }
  }, [isSidecarLounge]);

  React.useEffect(() => {
    const timeoutId = window.setTimeout(syncNativeAutoplayToState, 150);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isAutoPlay, syncNativeAutoplayToState]);

  useHeroVideoStateSync({
    heroVideoUrl,
    images,
    setCurrentSlideId,
    setIsHeroVideoPlaying,
  });

  const onNativeAutoplayButtonFound = React.useCallback(
    (nativeAutoplayButton) => {
      nativeAutoplayButtonRef.current = nativeAutoplayButton;
    },
    []
  );

  useDetachedControlsPosition({
    heroCarouselRef,
    onNativeAutoplayButtonFound,
    imagesLength: images.length,
    isAutoPlay,
    setDetachedControlsPosition,
  });

  const openGallery = () => {
    setGalleryStartSlideId(currentSlideId || images[0]?.imgId || '');
    setIsGalleryOpen(true);
  };

  const toggleAutoplay = () => {
    hasUserToggledAutoplayRef.current = true;
    setIsAutoPlay((prev) => !prev);
  };

  return (
    <>
      <div
        id={styles.loungeHeroCarousel}
        className="width-full container tw-relative tw-max-w-360 lg_tw-mt-10"
        ref={heroCarouselRef}
      >
        {/* will reenable after the August 12th release */}
        {/* <LoungeTypeBadge loungeId={loungeId} /> */}
        <Carousel
          id="lounge-hero"
          isNavigationOnSlides={true}
          shouldAutoPlay={isAutoPlay}
          onSlideChange={setCurrentSlideId}
          isSlideFullWidth={true}
        >
          {renderHeroSlides({
            heroSlides,
            loungeName: lounge.name,
            isHeroVideoPlaying,
            setIsHeroVideoPlaying,
            heroVideoFallbackImage,
            heroVideoFallbackImageMobile,
          })}
        </Carousel>
        <DetachedControls
          detachedControlsPosition={detachedControlsPosition}
          isAutoPlay={isAutoPlay}
          onToggleAutoplay={toggleAutoplay}
          // will reenable after the August 12th release (images.length > 1)
          showGalleryButton={false}
          onOpenGallery={openGallery}
        />
      </div>
      <GalleryModal
        isGalleryOpen={isGalleryOpen}
        onClose={() => setIsGalleryOpen(false)}
        loungeName={lounge.name}
        images={images}
        startId={galleryStartSlideId}
      />
    </>
  );
};

export default LoungeDetailsHeroCarousel;
